import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Undo2, Redo2, Clipboard, Sparkles, Copy, Scissors, Trash2,
  RefreshCw, CheckCircle, AlertCircle, Clock, FileText, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const EditPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;

  const getContent = () => {
    if (path.includes('/edit/history')) return <UndoRedo />;
    if (path.includes('/edit/clipboard')) return <ClipboardViewer />;
    if (path.includes('/edit/ai-format')) return <AIFixFormatting />;
    return <EditOverview navigate={navigate} />;
  };

  return <div className="space-y-6">{getContent()}</div>;
};

// Overview
const EditOverview = ({ navigate }) => {
  return (
    <>
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Edit Tools</h1>
        <p className="text-gray-500 mt-1">Safe editing tools with audit trail protection</p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card 
          className="hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => navigate('/edit/history')}
          data-testid="edit-undo-redo"
        >
          <CardContent className="p-6 text-center">
            <div className="p-3 bg-sky-100 rounded-xl inline-block mb-3">
              <Undo2 className="w-8 h-8 text-sky-600" />
            </div>
            <h3 className="font-semibold text-gray-900">Undo / Redo</h3>
            <p className="text-sm text-gray-500 mt-1">Reverse recent actions within session</p>
          </CardContent>
        </Card>

        <Card 
          className="hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => navigate('/edit/clipboard')}
          data-testid="edit-clipboard"
        >
          <CardContent className="p-6 text-center">
            <div className="p-3 bg-amber-100 rounded-xl inline-block mb-3">
              <Clipboard className="w-8 h-8 text-amber-600" />
            </div>
            <h3 className="font-semibold text-gray-900">Clipboard Viewer</h3>
            <p className="text-sm text-gray-500 mt-1">View and manage copied data</p>
          </CardContent>
        </Card>

        <Card 
          className="hover:shadow-md transition-shadow cursor-pointer bg-purple-50/50 border-purple-200"
          onClick={() => navigate('/edit/ai-format')}
          data-testid="edit-ai-format"
        >
          <CardContent className="p-6 text-center">
            <div className="p-3 bg-purple-100 rounded-xl inline-block mb-3">
              <Sparkles className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="font-semibold text-gray-900">AI Fix Formatting</h3>
            <p className="text-sm text-gray-500 mt-1">AI cleans and normalizes pasted data</p>
            <span className="text-xs bg-purple-200 text-purple-700 px-2 py-0.5 rounded mt-2 inline-block">AI Powered</span>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-amber-800">Edit Safety Notice</h3>
            <p className="text-sm text-amber-700 mt-1">
              Posted transactions cannot be deleted - only reversed. This maintains a complete audit trail
              for compliance and financial integrity.
            </p>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

// Undo/Redo
const UndoRedo = () => {
  const [history, setHistory] = useState([
    { id: 1, action: 'Created Check #1005', type: 'check', time: '2 minutes ago', canUndo: true },
    { id: 2, action: 'Modified Bill BILL-ABC123', type: 'bill', time: '5 minutes ago', canUndo: true },
    { id: 3, action: 'Added Vendor: Farm Supplies Inc', type: 'vendor', time: '10 minutes ago', canUndo: true },
    { id: 4, action: 'Posted Journal Entry JE-001', type: 'journal', time: '15 minutes ago', canUndo: false },
  ]);

  const handleUndo = (id) => {
    const item = history.find(h => h.id === id);
    setHistory(history.filter(h => h.id !== id));
    toast.success(`Undone: ${item.action}`);
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Undo / Redo</h1>
          <p className="text-gray-500 mt-1">Reverse recent actions within this session</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" disabled>
            <Undo2 className="w-4 h-4 mr-2" />Undo All
          </Button>
          <Button variant="outline" disabled>
            <Redo2 className="w-4 h-4 mr-2" />Redo
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Action History (This Session)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {history.map((item) => (
              <div key={item.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    item.type === 'check' ? 'bg-sky-100' :
                    item.type === 'bill' ? 'bg-amber-100' :
                    item.type === 'vendor' ? 'bg-emerald-100' : 'bg-purple-100'
                  }`}>
                    <FileText className={`w-4 h-4 ${
                      item.type === 'check' ? 'text-sky-600' :
                      item.type === 'bill' ? 'text-amber-600' :
                      item.type === 'vendor' ? 'text-emerald-600' : 'text-purple-600'
                    }`} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{item.action}</p>
                    <p className="text-sm text-gray-500">{item.time}</p>
                  </div>
                </div>
                {item.canUndo ? (
                  <Button variant="outline" size="sm" onClick={() => handleUndo(item.id)}>
                    <Undo2 className="w-4 h-4 mr-1" />Undo
                  </Button>
                ) : (
                  <span className="text-xs text-gray-400 px-3 py-1 bg-gray-100 rounded">
                    Posted - Cannot Undo
                  </span>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-50">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-slate-500 mt-0.5" />
          <div>
            <p className="text-sm text-slate-600">
              <strong>Note:</strong> Posted transactions cannot be undone. Use the Journal Entry reversal
              feature to correct posted entries while maintaining audit integrity.
            </p>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

// Clipboard Viewer
const ClipboardViewer = () => {
  const [clipboardItems, setClipboardItems] = useState([
    { id: 1, content: 'ABC Supplies Co\t$1,500.00\t6000', type: 'text', time: 'Just now' },
    { id: 2, content: 'Invoice #INV-2026-001', type: 'text', time: '5 minutes ago' },
  ]);

  const handleClear = (id) => {
    setClipboardItems(clipboardItems.filter(c => c.id !== id));
    toast.success('Item removed from clipboard');
  };

  const handlePaste = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard!');
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clipboard Viewer</h1>
          <p className="text-gray-500 mt-1">View and manage recently copied data</p>
        </div>
        <Button variant="outline" onClick={() => {
          setClipboardItems([]);
          toast.success('Clipboard cleared');
        }}>
          <Trash2 className="w-4 h-4 mr-2" />Clear All
        </Button>
      </div>

      {clipboardItems.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            <Clipboard className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Clipboard is empty</p>
            <p className="text-sm mt-1">Copy some data to see it here</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {clipboardItems.map((item) => (
            <Card key={item.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <code className="block bg-slate-100 p-3 rounded text-sm font-mono whitespace-pre-wrap">
                      {item.content}
                    </code>
                    <p className="text-xs text-gray-500 mt-2">{item.time}</p>
                  </div>
                  <div className="flex gap-1 ml-4">
                    <Button variant="ghost" size="sm" onClick={() => handlePaste(item.content)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleClear(item.id)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};

// AI Fix Formatting
const AIFixFormatting = () => {
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleFixFormatting = () => {
    if (!inputText.trim()) return;
    
    setIsProcessing(true);
    
    // Simulate AI processing
    setTimeout(() => {
      // Simple formatting fixes
      let fixed = inputText
        .replace(/\s+/g, ' ') // Normalize whitespace
        .replace(/,(\d{3})/g, '$1') // Remove comma in numbers
        .trim();
      
      setOutputText(fixed);
      setIsProcessing(false);
    }, 1000);
  };

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-purple-500" />
          AI Fix Formatting
        </h1>
        <p className="text-gray-500 mt-1">AI cleans and normalizes pasted data for import</p>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Input Data</CardTitle>
          </CardHeader>
          <CardContent>
            <textarea
              className="w-full h-48 p-3 border rounded-lg font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="Paste your data here...&#10;&#10;Example:&#10;ABC   Supplies  Co    $1,500.00    6000&#10;XYZ Corp    $2,300    7200"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
            />
            <Button 
              className="mt-3 w-full bg-purple-600 hover:bg-purple-700"
              onClick={handleFixFormatting}
              disabled={isProcessing || !inputText.trim()}
            >
              {isProcessing ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Processing...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" />Fix Formatting</>
              )}
            </Button>
          </CardContent>
        </Card>

        <Card className={outputText ? 'border-emerald-200 bg-emerald-50/30' : ''}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Output
              {outputText && <CheckCircle className="w-5 h-5 text-emerald-500" />}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <textarea
              className="w-full h-48 p-3 border rounded-lg font-mono text-sm resize-none bg-white"
              placeholder="Fixed data will appear here..."
              value={outputText}
              readOnly
            />
            {outputText && (
              <Button 
                className="mt-3 w-full" 
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(outputText);
                  toast.success('Copied to clipboard!');
                }}
              >
                <Copy className="w-4 h-4 mr-2" />Copy to Clipboard
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="bg-purple-50/50 border-purple-200">
        <CardContent className="p-4">
          <h3 className="font-semibold text-purple-800 mb-2">AI Capabilities:</h3>
          <ul className="text-sm text-purple-700 space-y-1">
            <li>• Normalize whitespace and remove extra spaces</li>
            <li>• Fix number formatting (commas, decimals)</li>
            <li>• Detect and fix column alignment</li>
            <li>• Identify duplicate lines</li>
            <li>• Standardize date formats</li>
          </ul>
        </CardContent>
      </Card>
    </>
  );
};

export default EditPage;
